import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useAppSelector } from '../../../../Store';
import { colors, css, fonts, frames } from '../../../../Styles';
import { FeedCurveSettingsModal, PageHeader, FeedCurveSpreading, FeedCurveTable, FeedCurveWithInfoBoxes, NewModal, MainContentPaddingTypes, NewButton } from '../../../../Components';
import { mediaQueriesNumbers, useMediaQuery } from '../../../../Styles/mediaQueries';
import { Pages } from '../../../../Navigation/root';
import { Graph_QL, DynamicDataObk, useFeedCurves, useMilestones, FeedCurve as FeedCurveValue, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveUpdates, useAnimal, useFeedComponents } from '../../../../Services';
import ShowFeedCurve from '../../../../Components/FeedCurves/Modals/showFeedCurve';
import { FeedCurveContext, FeedCurveDispatcherT, FeedCurveReducer, GraphTypes, IFeedCurveContext } from '../../../../Components/FeedCurves/_FeedCurves/feedCurveContext';
import { ButtonTypes } from '../../../../Definitions';

export default function FeedCurve() {
  const { t } = useTranslation();
  const location = useLocation();
  const [disableCurveNavigation, setDisableCurveNavigation] = useState(false);
  const [selectedFeedCurveId, setSelectedFeedCurveId] = useState<number>();
  const [modalState, setModalState] = useState<"ShowFeedCurve" | "Spreading" | "Settings" | "delete" | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined)

  // Media Quiries
  const mobileXs = useMediaQuery(`(max-width: 690px)`);
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);

  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm ?? "" })), [farm]);
  
  // Reducer
  const [_state, dispatch] = useReducer<Reducer<IFeedCurveContext, FeedCurveDispatcherT>>(FeedCurveReducer, {
    graphType: GraphTypes.feedCurveWithInfoBoxes,
    milestone: {
      selectedMilestone: undefined,
      selectedDay: undefined
    },
    component: undefined,
    error: { Elementid: 0, errorText: "" },
  } as IFeedCurveContext);
  
  // #region | Feed Curves data
  const { call: getFeedCurves, data: feedCurveState, maintainEntries: maintainFeedCurveEntries, deleteFeedCurve } = useFeedCurves(connection);
  const { call: getMilestones, data: milestoneState } = useMilestones(connection);
  const { call: getAnimalInfo, data: animalState } = useAnimal(connection);
  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(connection);
  
  const maintainFeedCurve = useCallback((changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurveValue, optimisticFeedCurve: FeedCurveValue}) => {
    const returnValue: Promise<[boolean, string]> = new Promise(() =>  [false, "Internal error"]); 
    if (maintainFeedCurveEntries) {
      return maintainFeedCurveEntries(changed, deleted, created, feedCurve) ?? returnValue; 
    }
    return returnValue
  },[maintainFeedCurveEntries])
  
  const fetchedFeedCurves = useMemo(() => {
    return feedCurveState && feedCurveState[domain] ? feedCurveState[domain].data.filter((feedCurve) => feedCurve.info?.deleted !== true) : [];
  },[domain, feedCurveState])

  
  const feedCurve = useMemo(() => {return fetchedFeedCurves.find((feedCurve) => feedCurve.feedCurveId === selectedFeedCurveId)},[fetchedFeedCurves, selectedFeedCurveId]);
  const milestoneOptions = useMemo(() => {
    return milestoneState[domain]?.data.filter((milestone) => !milestone.deleted).map((milestone) => ({label: milestone.name, id: milestone.milestone_id})) ?? []
  },[domain, milestoneState]) 

  useEffect(() => {
    if (!(feedCurveState[domain])) {
      getFeedCurves({fetchAmount: 50, ifFeedCurveBaseInfoOnly: true})
    }
    getAnimalInfo({})
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  },[domain, getFeedCurves, feedCurveState, feedComponentsState, getFeedComponentsInfo])

  useEffect(() => {
    if (!(milestoneState[domain])) {
      getMilestones({fetchAmount: 50})
    }
  }, [domain, getMilestones, milestoneState])
  
  const animals = useMemo(() => {
    return (animalState[domain]?.data || []).filter(a => a.feedcurve_id === feedCurve?.feedCurveId);
  }, [animalState, domain, feedCurve]);

  const usedComponentIds = useMemo(() => feedCurve?.components?.map((component) => component.componentId),[feedCurve?.components]);
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data.filter((feedComponent) => !feedComponent.deleted && !usedComponentIds?.includes(feedComponent.id)): undefined,[domain, feedComponentsState, usedComponentIds])
  const allFeedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data.filter((feedComponent) => !feedComponent.deleted): undefined,[domain, feedComponentsState])

  useEffect(() => {
    if (!selectedFeedCurveId) {
      return
    }
    const containsSettings = (feedCurve?.milestones && feedCurve.milestones.length === 0) || Boolean(feedCurve?.milestones?.find((milestone) => milestone.feedPlan))
    if (!feedCurve || !feedCurve.milestones || !containsSettings) {
      getFeedCurves({ids: [selectedFeedCurveId], ifSpreading: true})
    }
    
  },[feedCurve, getFeedCurves, selectedFeedCurveId]);

  // Get options from feedCurve Data and return the first to view if there isn't any selected from the feedCurves page.
  const feedCurvesOptions = useMemo(() => {
    const options = fetchedFeedCurves.map(feedCurve => ({
      label: feedCurve.name,
      id: feedCurve.feedCurveId
    }));
    if (!selectedFeedCurveId && location.state?.params?.selected) {
      setSelectedFeedCurveId(location.state.params.selected);
    } else if (!selectedFeedCurveId && options.length) {
      setSelectedFeedCurveId(options[0].id);
    }
    return options;
  }, [fetchedFeedCurves, selectedFeedCurveId, location.state?.params?.selected]);

  useEffect(() => {
    if (location.state?.params?.selected && !selectedFeedCurveId) {
      setSelectedFeedCurveId(location.state.params.selected);
    }
  }, [location.state, selectedFeedCurveId, domain]);
  // #endregion

  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.feedCurves}`, { defaultValue: "feed curves" })}
        subSelect={{
          options: feedCurvesOptions,
          onChange: (e) => setSelectedFeedCurveId(Number(e?.id ?? 0)),
          defaultValue: feedCurvesOptions.find(option => option.label === feedCurve?.name)
        }}
        optionButtons={{
          contents: [
            t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"}), 
            // t("feedCurvePages.feedCurve.corrections", {defaultValue: "corrections"}), 
            t("feedCurvePages.feedCurve.settings", { defaultValue: "settings" }),
            t("feedCurvePages.feedCurve.deleteFeedcurve", {defaultValue: "delete feedcurve"})
          ],
          onClick: (i => {
            if (i === 0) {setModalState("Spreading")} 
            else if (i === 1) { setModalState("Settings") }
            else if (i === 2) { setModalState("delete") }
          })
        }}
      />
      <frames._page_inner_content>
        <FeedCurveContext.Provider value={{ state: _state, dispatch }}>
        <div style={{display: "grid", overflow: "hidden", height: "100%", width: "100%", gridTemplateColumns: "1fr", gridTemplateRows: (isScreenHeightMax || mobileXs) ? "1fr" : "350px 1fr"}}>
          {/* FeedCurveViewer */}
          {(isScreenHeightMax || mobileXs) ? <></>
          : <div style={{gridArea: "1/1/2/2", overflow: "hidden", display: "flex", alignItems: "center"}}>
              {feedCurve?.milestones &&
                <FeedCurveWithInfoBoxes animals={animals} data={feedCurve} invertedColors/> 
            }
          </div>}

          {/* FeedCurveTable */}
          <div style={{ gridArea: (isScreenHeightMax || mobileXs) ? "1/1/2/2" : "2/1/3/2", overflow: "hidden", position: "relative" }}>
            {feedCurve && 
              <FeedCurveTable 
                milestoneOptions={milestoneOptions}
                setDisableCurveNavigation={setDisableCurveNavigation}
                watchFeedCurve={() => setModalState("ShowFeedCurve")}
                feedCurve={feedCurve}
                maintainFeedCurve={maintainFeedCurve}
                allFeedComponents={allFeedComponents}
                feedComponents={feedComponents}
              />
            }
          </div>
          </div>
        </FeedCurveContext.Provider>
      </frames._page_inner_content>
      

      {feedCurve && modalState === "ShowFeedCurve" && farm &&
        <ShowFeedCurve
          farm={farm}
          feedCurve={feedCurve}
          handleClose={() => { setModalState(undefined) }}
        />
      }
      
      {feedCurve && modalState === "Spreading" && 
        <FeedCurveSpreading feedCurve={feedCurve} onClose={() => setModalState(undefined)}/>
      }

      {feedCurve && modalState === "Settings" && 
        <FeedCurveSettingsModal closeModal={() => setModalState(undefined)} feedCurve={feedCurve} />
      }


      {feedCurve && modalState === "delete" && 
        <NewModal
          modalState={modalState === "delete"}
          predefinedButtons={{
            onClose: () => setModalState(undefined)
          }}
          header={t("feedCurvePages.feedCurve.deleteFeedcurve", {defaultValue: "delete feedcurve"})}
          overrides={{
            mainContentPadding: MainContentPaddingTypes.secondary,
            overlayColor: colors.blackPrimary+"99"
          }}
          mainContent={
            <p style={{ ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyLightTertiary }}>{`${t("feedCurvePages.feedCurve.confirmDelete", {defaultValue: "Are you sure you want to delete this feedcurve"})}?`}</p>
          }
          footerContent = {
            <NewButton
              content={t("buttons.confirm", {defaultValue: "confirm"})}
              onClick={async () => {
                if (deleteFeedCurve) {
                  const res = await deleteFeedCurve(feedCurve.feedCurveId);

                  console.log(res)

                  if (res.success) {
                    setModalState(undefined)
                    setSelectedFeedCurveId(feedCurvesOptions[((feedCurvesOptions.findIndex(f => f.id === selectedFeedCurveId))+1)%feedCurvesOptions.length].id)
                  }
                  if (!res.success) {
                    setMessage(res.message)
                    setTimeout(() => {
                      setMessage(undefined);
                    }, 2000);
                  }
                }
              }}
            />
          }
          footerMessage={message}
        />
      }
    
    </frames.Page_inner>
  );
}